import { useEffect, useMemo, useContext, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { ClientContext } from 'react-fetching-library';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import { UserContext } from '@app/providers';
import showServerError from '@shared/lib/showError';
import { qhsDebug } from '@shared/lib/qhsDebug';
import { HipaaCheck } from '@features/hipaa-agreement';
import { checkPermission } from '@shared/lib/checkPermissions';
import { shouldSkipDefaultNavigationForOAuth } from '@shared/lib/oauthReturnUrl';
import { encodePathSegment, isSafePathId } from '@shared/lib/pathSegment';
import permissions from '@shared/config/permissions';
import { useSelector, useDispatch } from 'react-redux';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
  logout as logoutAction,
  getMetadata,
  getUser,
  setUser as setUserAction,
  setHipaaCheckParams as setHipaaCheckParamsAction,
  setHipaaPassedAccountUuid,
  setMetadata as setMetadataAction,
  setBlockedAccounts,
  setInitPath,
  closeInfoPane,
  clearActivePatient,
  getIsLogin,
  setIsLogin,
  setIsLoginStatus,
  getTransitionWithVariable,
  REQUEST_STATUS,
  navigateToRoute,
} from 'redux_store';
import { matchPath } from 'react-router';
import { ALL_PRIVATE_ROUTES, ALL_PUBLIC_ROUTES, ALL_ROUTES } from '@app/router';
import usePreferredLanguageSync from './usePreferredLanguageSync';

export const tryToParseRouteFromString = (path, routes) => {
  let parsedRoute;
  (routes || ALL_PRIVATE_ROUTES).forEach(i => {
    const match = matchPath({ path: i.path, end: true }, path);
    if (match) {
      parsedRoute = {
        path: i.path,
        params: {
          ...match.params,
        },
        title: i.additional.title,
      };
    }
  });
  return parsedRoute;
};

let metadata_store = null;

// Helper function to determine default route based on account type
const getDefaultRouteForAccount = (metadata, disabledFeatures = []) => {
  const accountPermissions = metadata?.currentAccountPermissions;

  // Check if populationHealth is accessible
  const populationHealthPermission = ALL_ROUTES.dashboard.populationHealth.additional?.permission;
  const populationHealthDisabledId = ALL_ROUTES.dashboard.populationHealth.additional?.disabledId;

  const hasPopulationHealthPermission = checkPermission(
    accountPermissions,
    populationHealthPermission
  );
  const isPopulationHealthEnabled = !disabledFeatures.includes(populationHealthDisabledId);
  const canAccessPopulationHealth = hasPopulationHealthPermission && isPopulationHealthEnabled;

  if (canAccessPopulationHealth) {
    return {
      path: ALL_ROUTES.dashboard.populationHealth.path,
      params: {
        currentAccountUuid: metadata.currentAccount.uuid,
      },
      title: ALL_ROUTES.dashboard.populationHealth.additional.title,
      clearBreadcrumbs: true,
    };
  }

  // Fallback: check if DS account
  const isDS = checkPermission(accountPermissions, permissions.ACCOUNTS_GET);
  if (isDS) {
    return {
      path: ALL_ROUTES.account.listDS.path,
      params: {
        currentAccountUuid: metadata.currentAccount.uuid,
      },
      title: ALL_ROUTES.account.listDS.additional.title,
      clearBreadcrumbs: true,
    };
  }

  // Default fallback: dashboard root
  return {
    path: ALL_ROUTES.dashboard.root.path,
    params: {
      currentAccountUuid: metadata.currentAccount.uuid,
    },
    title: ALL_ROUTES.dashboard.root.additional.title,
    clearBreadcrumbs: true,
  };
};

// Helper function to create route object with standard structure
const createRouteObject = (routeConfig, currentAccountUuid, additionalParams = {}) => {
  return {
    path: routeConfig.path,
    params: {
      currentAccountUuid,
      ...additionalParams,
    },
    title: routeConfig.additional?.title,
    clearBreadcrumbs: true,
    removeQueryString: true,
  };
};

const UserProvider = ({ children }) => {
  const {
    user,
    hipaaCheckParams,
    hipaaPassedAccountUuid,
    metadata,
    blockedAccounts,
    isLogin,
    status,
    initPath,
  } = useSelector(state => state.user);
  const { disabledFeatures } = useSelector(state => state.appSettings);
  const dispatch = useDispatch();
  const clientContext = useContext(ClientContext);
  const { notifications, errors } = useSelector(state => state.i18n);
  const userUuid = user ? user.uuid : null;
  // Backend-stored UI language wins over the local cache (null = never
  // chosen, left alone). See the hook for the reload rationale.
  usePreferredLanguageSync(user ? user.preferredLanguage : null);
  // Wrapped setter so the diagnostic timeline catches every transition
  // of `changeAccountLoading`. The flag drives the first gate in
  // ProtectedRoute, so a stuck `true` value is one of the suspect
  // forever-loader paths.
  const [_changeAccountLoading, _setChangeAccountLoading] = useState(true);
  const setChangeAccountLoading = next => {
    qhsDebug('UserProvider:setChangeAccountLoading', { next });
    _setChangeAccountLoading(next);
  };
  const changeAccountLoading = _changeAccountLoading;
  const OLD_PATH_FOR_SUPPORT_REDIRECT = {
    '/dashboard': ALL_ROUTES.dashboard.root,
    '/accounts/list': ALL_ROUTES.account.listDS,
    '/patients/list': ALL_ROUTES.patient.list,
    '/orders/list': ALL_ROUTES.order.list,
    '/assessments/list': ALL_ROUTES.assessment.list,
    '/resources/list': ALL_ROUTES.resource.list,
    '/claimsReporting': ALL_ROUTES.claim.list,
    '/claimReport/list': ALL_ROUTES.claimReport.list,
    '/reportables/list': ALL_ROUTES.reportable.list,
  };

  const setMetadata = newMetadata => {
    if (!isEqual(metadata, newMetadata)) {
      dispatch(setMetadataAction(newMetadata));
    }
  };

  useEffect(() => {
    metadata_store = metadata;
  }, [metadata]);

  // fetch metadata if user exist
  useEffect(() => {
    if (userUuid) {
      refreshMetadata();
    }
  }, [userUuid]);

  const refreshMetadata = () => {
    dispatch(getMetadata());
  };

  const checkHipaa = (newUser, newMetadata, newRoute) => {
    dispatch(
      setHipaaCheckParamsAction({
        newUser: newUser,
        newMetadata: newMetadata,
        newRoute: newRoute,
      })
    );
  };

  // The switch was refused (or never sent): stay on the current account,
  // or go to sign-in when there is none.
  const leaveFailedAccountChange = () => {
    dispatch(
      enqueueSnackbar({
        message: notifications.noPermissions,
        options: {
          variant: SNACKBAR_VARIANTS.warning,
        },
      })
    );
    if (metadata?.currentAccount?.uuid) {
      dispatch(
        navigateToRoute({
          route: {
            clearBreadcrumbs: true,
            path: ALL_ROUTES.root.path,
            params: { currentAccountUuid: metadata?.currentAccount?.uuid },
            title: ALL_ROUTES.root.title || 'Dashboard',
          },
        })
      ).then(() => {
        setChangeAccountLoading(false);
      });
    } else {
      dispatch(
        navigateToRoute({
          route: {
            path: ALL_ROUTES.login.path,
            clearBreadcrumbs: true,
          },
        })
      ).then(() => {
        setChangeAccountLoading(false);
      });
    }
  };

  const changeAccount = async (accountUuid, route) => {
    setChangeAccountLoading(true);
    // The account id comes from the address bar and fires this credentialed
    // POST with no click: a crafted link must not be able to aim it at
    // another API route (react-router hands '%2F' back as '/').
    if (!isSafePathId(accountUuid)) {
      leaveFailedAccountChange();
      return;
    }
    const { payload, error } = await clientContext.query({
      method: 'POST',
      endpoint: `/auth/token/account/${encodePathSegment(accountUuid)}`,
    });
    if (!error && payload) {
      const { user: newUser, authMetadata } = payload;
      if (!authMetadata) {
        showServerError(dispatch, errors.doNotHaveAccessToAnyAccounts);
        return;
      }
      checkHipaa(newUser, authMetadata, route);
    } else {
      leaveFailedAccountChange();
    }
  };

  const handleSuccessHipaaCheck = () => {
    const { newUser, newMetadata, newRoute } = hipaaCheckParams;
    qhsDebug('UserProvider:handleSuccessHipaaCheck:enter', {
      hasNewUser: !!newUser,
      newUserUuid: newUser?.uuid,
      hasNewMetadata: !!newMetadata,
      newRoute: newRoute?.path,
      isMetadataAlreadyExists: !!metadata?.currentAccount?.uuid,
    });
    dispatch(setUserAction(newUser));
    if (newMetadata === null || Object.keys(newMetadata).length === 0) {
      showServerError(dispatch, errors.doNotHaveAccessToAnyAccounts);
      return;
    }
    if (newRoute) {
      dispatch(setInitPath(newRoute));
    }
    // Before the metadata lands: ProtectedRoute opens the account's pages
    // only once this matches the current account.
    dispatch(setHipaaPassedAccountUuid(newMetadata.currentAccount?.uuid ?? null));
    // An account switch in this tab keeps the store. The info pane lives
    // outside the routed pages and never refetches, so it would keep showing
    // the previous practice's patient (name, MRN, contacts) under the new
    // account; the active patient would prefill the next patient's forms.
    const previousAccountUuid = metadata?.currentAccount?.uuid;
    if (previousAccountUuid && previousAccountUuid !== newMetadata.currentAccount?.uuid) {
      dispatch(closeInfoPane());
      dispatch(clearActivePatient());
    }
    // Force metadata update by dispatching directly to Redux
    dispatch(setMetadataAction({ ...newMetadata, route: newRoute }));

    // Check if we need to trigger redirect manually (if metadata already existed)
    const isMetadataAlreadyExists = metadata?.currentAccount?.uuid;

    // The OAuth consent screen opens in a fresh tab, so ProtectedRoute's HIPAA gate always runs
    // there and lands here with metadata loaded. The redirect below rebuilds the URL from the
    // route path only, which drops `?request_id` and the page shows "No connection request was
    // found". Stay on the current URL instead - same rule as the metadata effect below.
    if (
      isMetadataAlreadyExists &&
      shouldSkipDefaultNavigationForOAuth(window.location.pathname, window.location.search)
    ) {
      qhsDebug('UserProvider:handleSuccessHipaaCheck:skippedForOAuth', {});
      setChangeAccountLoading(false);
      dispatch(setInitPath(null));
    } else if (isMetadataAlreadyExists) {
      const { pathname } = location;
      let finalNewRoute = newRoute || initPath;

      if (!finalNewRoute || finalNewRoute.isUnknown) {
        if (finalNewRoute) {
          const parsedRoute = tryToParseRouteFromString(finalNewRoute?.path, ALL_PUBLIC_ROUTES);
          const parsedOldRoute = OLD_PATH_FOR_SUPPORT_REDIRECT[finalNewRoute?.path];

          if (parsedOldRoute) {
            finalNewRoute = createRouteObject(parsedOldRoute, newMetadata.currentAccount.uuid);
          } else if (parsedRoute) {
            finalNewRoute = getDefaultRouteForAccount(newMetadata, disabledFeatures);
          } else {
            finalNewRoute = {
              path: finalNewRoute?.path || pathname,
            };
          }
        } else {
          finalNewRoute = getDefaultRouteForAccount(newMetadata, disabledFeatures);
        }
      }

      const manualRedirectRoute = {
        clearBreadcrumbs: true,
        path: finalNewRoute.path,
        params: { currentAccountUuid: newMetadata?.currentAccount?.uuid, ...finalNewRoute.params },
        title: finalNewRoute.title,
      };

      dispatch(
        navigateToRoute({
          route: manualRedirectRoute,
        })
      )
        .then(() => {
          setChangeAccountLoading(false);
        })
        .catch(error => {
          setChangeAccountLoading(false);
        });

      dispatch(setInitPath(null));
    }

    dispatch(setHipaaCheckParamsAction(null));
    dispatch(setIsLogin(true));
    dispatch(setIsLoginStatus(REQUEST_STATUS.succeeded));
    qhsDebug('UserProvider:handleSuccessHipaaCheck:exit', {
      tookExistingMetadataBranch: !!metadata?.currentAccount?.uuid,
    });
  };

  // Cancel on the HIPAA agreement. At sign-in there is nothing to go
  // back to, so sign out. During an account switch the token has
  // already moved to the new account (POST /auth/token/account), so
  // "cancel" means switching back: re-issue the token for the account
  // the user came from and return to the page they were on. That
  // account's own check passes (they were in it), which also clears
  // the loader through the normal success path.
  const handleFailHipaaCheck = () => {
    // Only an account that passed its own check is somewhere to go back
    // to. After a reload the current account is the one being checked, so
    // cancelling there signs out instead of re-asking in a loop.
    const previousUuid = hipaaPassedAccountUuid;
    if (!previousUuid) {
      dispatch(logoutAction());
      return;
    }
    dispatch(setHipaaCheckParamsAction(null));
    const parsedRoute = tryToParseRouteFromString(location.pathname);
    const backRoute = parsedRoute
      ? { ...parsedRoute, params: { ...parsedRoute.params, currentAccountUuid: previousUuid } }
      : undefined;
    changeAccount(previousUuid, backRoute);
  };

  const setUser = newUser => {
    dispatch(setUserAction(newUser));
  };

  const handleSwitchHipaaAccount = useCallback(() => {
    const {
      newMetadata: { accounts, currentAccount },
    } = hipaaCheckParams;
    dispatch(setHipaaCheckParamsAction(null));

    const newBlockedAccounts = uniq([...blockedAccounts, currentAccount.uuid]);
    dispatch(setBlockedAccounts(newBlockedAccounts));
    const accountsToCheck = accounts.filter(item => !newBlockedAccounts.includes(item.uuid));

    if (accountsToCheck.length) {
      changeAccount(accountsToCheck[0].uuid);
      dispatch(
        enqueueSnackbar({
          message: getTransitionWithVariable(notifications.youHaveBeenSwitched, {
            name: accountsToCheck[0].businessName,
          }),
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
        })
      );
      dispatch(setInitPath(null));
    } else {
      dispatch(logoutAction());
    }
  }, [blockedAccounts, hipaaCheckParams]);

  useEffect(() => {
    qhsDebug('UserProvider:metadataEffect:fire', {
      currentAccount: metadata?.currentAccount?.uuid || null,
      hasInitPath: !!initPath,
      initPath: initPath ? { path: initPath.path, isUnknown: initPath.isUnknown } : null,
    });
    if (metadata?.currentAccount?.uuid) {
      const { pathname } = location;

      // Don't redirect if user is on password reset page (completing password reset flow)
      if (pathname === '/password/reset' || pathname.startsWith('/password/reset')) {
        setChangeAccountLoading(false);
        return;
      }

      if (
        initPath &&
        initPath.params?.currentAccountUuid &&
        initPath.params?.currentAccountUuid !== metadata?.currentAccount?.uuid
      ) {
        setChangeAccountLoading(true);
        changeAccount(initPath.params.currentAccountUuid, initPath);
      } else {
        let newRoute = initPath;
        if (!initPath || initPath.isUnknown) {
          if (initPath) {
            const parsedRoute = tryToParseRouteFromString(initPath?.path, ALL_PUBLIC_ROUTES);
            const parsedOldRoute = OLD_PATH_FOR_SUPPORT_REDIRECT[initPath?.path];

            if (parsedOldRoute) {
              newRoute = createRouteObject(parsedOldRoute, metadata.currentAccount.uuid);
            } else if (parsedRoute) {
              newRoute = getDefaultRouteForAccount(metadata, disabledFeatures);
            } else {
              newRoute = {
                path: initPath?.path || pathname,
              };
            }
          } else {
            newRoute = getDefaultRouteForAccount(metadata, disabledFeatures);
          }
        }

        const finalRoute = {
          clearBreadcrumbs: true,
          path: newRoute.path,
          params: { currentAccountUuid: metadata?.currentAccount?.uuid, ...newRoute.params },
          title: newRoute.title,
        };

        // Skip this in-app default-route navigation when the page owns its own URL:
        //  - a valid login `?returnUrl=` means SignInForm is about to
        //    window.location.assign back to it (cross-origin, full page unload) on
        //    this same success transition, so an in-app route-change to the dashboard
        //    first is redundant and flashes; and
        //  - the consent screen (`/oauth/consent?request_id=`) must keep its query -
        //    `initPath` is parsed from the pathname only, so navigating to it here would
        //    drop `request_id` and the page would show "No connection request was found".
        if (shouldSkipDefaultNavigationForOAuth(window.location.pathname, window.location.search)) {
          qhsDebug('UserProvider:metadataEffect:skippedForOAuth', {});
          // This branch skips the navigateToRoute .then()/.catch() below, which is the only
          // other place `changeAccountLoading` is cleared on this path. The flag inits `true`
          // and App.jsx / ProtectedRoute gate the whole tree on it, so clear it here (and drop
          // the now-consumed initPath) - otherwise an authenticated user landing on
          // /oauth/consent hangs on the splash screen forever. The pre-existing returnUrl case
          // gets away without this only because SignInForm is about to full-page unload the tab.
          setChangeAccountLoading(false);
          dispatch(setInitPath(null));
          return;
        }

        qhsDebug('UserProvider:metadataEffect:navigate', {
          path: finalRoute.path,
          accountUuid: finalRoute.params.currentAccountUuid,
        });
        dispatch(
          navigateToRoute({
            route: finalRoute,
          })
        )
          .then(() => {
            qhsDebug('UserProvider:metadataEffect:navigate:resolved', { path: finalRoute.path });
            setChangeAccountLoading(false);
          })
          .catch(error => {
            qhsDebug('UserProvider:metadataEffect:navigate:rejected', {
              path: finalRoute.path,
              error: error?.message || String(error),
            });
            setChangeAccountLoading(false);
          });
        dispatch(setInitPath(null));
      }
    }
  }, [metadata?.currentAccount?.uuid]);

  useEffect(() => {
    dispatch(getIsLogin());

    const { pathname } = location;
    const parsedRoute = tryToParseRouteFromString(pathname);

    if (parsedRoute) {
      dispatch(setInitPath(parsedRoute));
    } else {
      const parsedPublicRoute = tryToParseRouteFromString(pathname, ALL_PUBLIC_ROUTES);

      if (!parsedPublicRoute) {
        dispatch(
          setInitPath({
            path: pathname,
            isUnknown: true,
          })
        );
      }
      setChangeAccountLoading(false);
    }

    if (isLogin) {
      dispatch(getUser());
    }
  }, []);

  useEffect(() => {
    // Sign-in flips `isLogin` before the HIPAA check has run (the
    // login reducer, and every response carrying Authorization-Expires
    // — the check's own GET included). Loading the user here would pull
    // in the metadata and navigate into the app behind the agreement
    // dialog, so wait until the check has resolved: success sets the
    // user itself, cancel signs out.
    if (isLogin && !user && !hipaaCheckParams) {
      dispatch(getUser());
    }
    if (isLogin === false) {
      const { pathname } = location;
      const parsedRoute = tryToParseRouteFromString(pathname);
      if (!!parsedRoute) {
        dispatch(
          navigateToRoute({
            route: {
              path: ALL_ROUTES.login.path,
              clearBreadcrumbs: true,
            },
          })
        ).then(() => {
          setChangeAccountLoading(false);
        });
      }
    }
  }, [isLogin, hipaaCheckParams]);

  useEffect(() => {
    if (status === REQUEST_STATUS.failed) {
      dispatch(
        navigateToRoute({
          route: {
            path: ALL_ROUTES.login.path,
            clearBreadcrumbs: true,
          },
        })
      ).then(() => {
        setChangeAccountLoading(false);
      });
    }
  }, [status]);

  const eventListener = () => {
    /*
         handle changing route outside the dashboard
         case: browser back button click
       */
    let newRoute = tryToParseRouteFromString(document.location.pathname);
    const accountUuid =
      metadata_store && metadata_store.currentAccount ? metadata_store.currentAccount.uuid : null;

    if (
      newRoute?.params?.currentAccountUuid &&
      newRoute.params.currentAccountUuid !== accountUuid
    ) {
      setChangeAccountLoading(true);
      changeAccount(newRoute.params.currentAccountUuid, newRoute);
    }
  };

  useEffect(() => {
    window.addEventListener('popstate', eventListener, true);
    return () => {
      window.removeEventListener('popstate', eventListener);
    };
  }, []);

  // memoize a provider value
  const value = useMemo(
    () => ({
      changeAccount,
      checkHipaa,
      handleFailHipaaCheck,
      setUser,
      changeAccountLoading,
    }),
    [user, metadata, changeAccount, changeAccountLoading]
  );

  return (
    <UserContext.Provider value={value}>
      {children}
      {hipaaCheckParams && (
        <HipaaCheck
          user={hipaaCheckParams.newUser}
          metadata={hipaaCheckParams.newMetadata}
          onSuccess={handleSuccessHipaaCheck}
          onFail={handleFailHipaaCheck}
          onSwitchAccount={handleSwitchHipaaAccount}
        />
      )}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default UserProvider;
